/**
 * Banner zgód na cookies (VP-312) — zastąpił Cookiebota, do którego straciliśmy dostęp.
 *
 * Zasady, które ten moduł realizuje:
 *  - nic poza kategorią „niezbędne" nie jest domyślnie włączone,
 *  - GTM ładuje się zawsze, ale w trybie Consent Mode v2 `denied` (default siedzi w
 *    index.html, PRZED kontenerem) — tagi odpalają dopiero po `consent update`,
 *  - FirstPromoter jest zablokowany w index.html przez `type="text/plain"` i odpala się
 *    dopiero, gdy biblioteka odblokuje kategorię marketingową,
 *  - PostHog startuje wyłącznie po zgodzie analitycznej (session recording!),
 *  - każda decyzja usera leci na `/api/consent` jako dowód zgody.
 */
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./cookie-consent-theme.css";
import i18n, { getBaseLanguage } from "@/i18n";
import { getAuthToken } from "@/lib/queryClient";
import { initPostHog, stopPostHog } from "@/lib/posthog";
import { useConsentStore, type ConsentSignals } from "./consent-store";
import { buildTranslations, type BannerLanguage } from "./translations";

/** Podbij, gdy zmieni się lista trackerów lub treść zgód — user zostanie zapytany ponownie. */
export const CONSENT_REVISION = 1;

/** Wersja polityki prywatności, którą user widzi przy wyrażaniu zgody. */
export const CONSENT_POLICY_VERSION = "2026-08";

type ConsentSource = "first" | "change";

declare global {
  interface Window {
    // Musi być identyczne z deklaracjami w lib/gtm.ts i pages/sales.tsx (TS2717 przy rozjeździe).
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function bannerLanguage(): BannerLanguage {
  return getBaseLanguage(i18n.language) === "en" ? "en" : "pl";
}

function readSignals(): ConsentSignals {
  return {
    analytics: CookieConsent.acceptedCategory("analytics"),
    marketing: CookieConsent.acceptedCategory("marketing"),
    functional: CookieConsent.acceptedCategory("functional"),
  };
}

function grant(accepted: boolean): "granted" | "denied" {
  return accepted ? "granted" : "denied";
}

/**
 * FirstPromotera nie da się odładować — raz wstrzyknięty skrypt żyje do końca sesji.
 * Jeśli user cofnie zgodę marketingową, jedynym uczciwym wyjściem jest przeładowanie
 * strony (skrypt zostanie wtedy z powrotem zablokowany przez `type="text/plain"`).
 */
let marketingScriptsLoaded = false;

function applyConsent(): void {
  const signals = readSignals();

  window.gtag?.("consent", "update", {
    analytics_storage: grant(signals.analytics),
    ad_storage: grant(signals.marketing),
    ad_user_data: grant(signals.marketing),
    ad_personalization: grant(signals.marketing),
    functionality_storage: grant(signals.functional),
    personalization_storage: grant(signals.functional),
  });
  window.dataLayer?.push({ event: "consent_update", ...signals });

  if (signals.analytics) {
    initPostHog();
  } else {
    stopPostHog();
  }

  useConsentStore.getState().setSignals(signals);

  // Przeładowanie NA KOŃCU: cofnięcie zgód musi zdążyć wejść w życie (consent update,
  // stop PostHoga, wygaszenie czatu przez store), bo reload jest asynchroniczny.
  if (signals.marketing) {
    marketingScriptsLoaded = true;
  } else if (marketingScriptsLoaded) {
    marketingScriptsLoaded = false;
    window.location.reload();
  }
}

async function recordProof(source: ConsentSource): Promise<void> {
  const cookie = CookieConsent.getCookie();
  const token = getAuthToken();

  try {
    await fetch("/api/consent", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        consentId: cookie.consentId,
        categories: cookie.categories,
        revision: cookie.revision,
        policyVersion: CONSENT_POLICY_VERSION,
        languageCode: cookie.languageCode,
        source,
      }),
      keepalive: true,
    });
  } catch (err) {
    // Dowód zgody to ślad audytowy — jego brak nie może psuć UI ani cofać wyboru usera.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[consent] nie udało się zapisać dowodu zgody", err);
    }
  }
}

export async function initCookieConsent(): Promise<void> {
  await CookieConsent.run({
    revision: CONSENT_REVISION,
    guiOptions: {
      consentModal: { layout: "box", position: "bottom left", equalWeightButtons: true },
      preferencesModal: { layout: "box", equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        autoClear: { cookies: [{ name: /^_ga/ }, { name: "_gid" }, { name: /^ph_/ }] },
      },
      marketing: {
        autoClear: { cookies: [{ name: /^_fb/ }, { name: /^_fpr/ }] },
      },
      functional: {
        autoClear: { cookies: [{ name: /^intercom-/ }] },
      },
    },
    language: {
      default: bannerLanguage(),
      translations: buildTranslations(),
    },
    // Przy pierwszej decyzji biblioteka woła onFirstConsent I onConsent. Stan aplikujemy
    // wyłącznie w onConsent (leci też przy każdym wejściu), inaczej `consent_update`
    // wpadałby do dataLayer dwa razy i tagi GTM odpalałyby się podwójnie.
    onFirstConsent: () => void recordProof("first"),
    onConsent: () => applyConsent(),
    onChange: () => {
      applyConsent();
      void recordProof("change");
    },
  });

  i18n.on("languageChanged", (language: string) => {
    void CookieConsent.setLanguage(getBaseLanguage(language) === "en" ? "en" : "pl");
  });
}

/** Otwiera panel zgód — user musi móc zmienić decyzję w każdej chwili. */
export function showCookiePreferences(): void {
  CookieConsent.showPreferences();
}
